import React from "react";

const PreviewStep = ({ data, onSave, onBack, saving, isEditMode = false }) => {
  const getReportTypeLabel = (type) => {
    switch (type) {
      case "roadmap":
        return "Roadmap";
      case "data-quality":
        return "Data Quality";
      case "relationship":
        return "Relationship Analysis";
      default:
        return type;
    }
  };

  const configuration = data.configuration || {};

  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-2">
        {isEditMode ? "Review Changes" : "Preview & Save"}
      </h2>
      <p className="text-gray-400 mb-6">
        {isEditMode
          ? "Review your changes before updating the report"
          : "Review your report configuration before saving"}
      </p>

      <div className="space-y-6">
        {/* Basic Info Preview */}
        <section className="bg-gray-900 rounded-lg border border-gray-700 p-4">
          <h3 className="text-lg font-medium text-white mb-4">
            Basic Information
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Name:</span>
              <span className="text-white">{data.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Type:</span>
              <span className="text-white">
                {getReportTypeLabel(data.report_type)}
              </span>
            </div>
            {/* Entity Type not shown for relationship reports - they configure Source Entity Type in Configuration */}
            {data.report_type !== "relationship" && (
              <div className="flex justify-between">
                <span className="text-gray-400">Entity Type:</span>
                <span className="text-white">
                  {data.entity_type || "Not specified"}
                </span>
              </div>
            )}
            {data.description && (
              <div className="flex justify-between">
                <span className="text-gray-400">Description:</span>
                <span className="text-white">{data.description}</span>
              </div>
            )}
            {data.category && (
              <div className="flex justify-between">
                <span className="text-gray-400">Category:</span>
                <span className="text-white">{data.category}</span>
              </div>
            )}
            {data.tags && data.tags.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Tags:</span>
                <span className="text-white">{data.tags.join(", ")}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-400">Shared:</span>
              <span className="text-white">
                {data.is_shared ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </section>

        {/* Configuration Preview */}
        <section className="bg-gray-900 rounded-lg border border-gray-700 p-4">
          <h3 className="text-lg font-medium text-white mb-4">Configuration</h3>
          {data.report_type === "roadmap" && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Default Zoom:</span>
                <span className="text-white capitalize">
                  {configuration.defaultZoom || "Year"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Clustering:</span>
                <span className="text-white">
                  {configuration.enableClustering !== false
                    ? "Enabled"
                    : "Disabled"}
                </span>
              </div>
              {configuration.clusterByTypes &&
                configuration.clusterByTypes.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Cluster By:</span>
                    <span className="text-white">
                      {configuration.clusterByTypes
                        .map((t) => t.replace("-", " "))
                        .join(", ")}
                    </span>
                  </div>
                )}
              <div className="flex justify-between">
                <span className="text-gray-400">Show Successors:</span>
                <span className="text-white">
                  {configuration.showSuccessors !== false ? "Yes" : "No"}
                </span>
              </div>
            </div>
          )}

          {data.report_type === "data-quality" && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Selected Columns:</span>
                <span className="text-white">
                  {(configuration.selectedAttributes || []).length +
                    (configuration.selectedCustomAttributes || []).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Grouping:</span>
                <span className="text-white">
                  {configuration.enableGrouping ? "Enabled" : "Disabled"}
                </span>
              </div>
              {configuration.groupBy && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Group By:</span>
                  <span className="text-white capitalize">
                    {configuration.groupBy}
                  </span>
                </div>
              )}
            </div>
          )}

          {data.report_type === "relationship" && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Analysis Type:</span>
                <span className="text-white capitalize">
                  {(configuration.variant || "dependency").replace("-", " ")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Source Entity Type:</span>
                <span className="text-white capitalize">
                  {configuration.sourceEntityType || "Not specified"}
                </span>
              </div>
              {/* Target Entity Type is only applicable for matrix variant */}
              {configuration.targetEntityType &&
                configuration.variant === "matrix" && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Target Entity Type:</span>
                    <span className="text-white capitalize">
                      {configuration.targetEntityType}
                    </span>
                  </div>
                )}
              {/* Analysis Depth is applicable for dependency and traceability */}
              {["dependency", "traceability"].includes(
                configuration.variant || "dependency"
              ) && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Analysis Depth:</span>
                  <span className="text-white">
                    {configuration.analysisDepth || 2} hop(s)
                  </span>
                </div>
              )}
              {/* Direction is applicable for dependency reports */}
              {(configuration.variant || "dependency") === "dependency" && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Direction:</span>
                  <span className="text-white capitalize">
                    {configuration.analysisDirection || "Both"}
                  </span>
                </div>
              )}
              {configuration.selectedRelationshipTypes?.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Relationship Types:</span>
                  <span className="text-white">
                    {configuration.selectedRelationshipTypes.length} selected
                  </span>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          disabled={saving}
          className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => onSave(false)}
            disabled={saving}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            {saving
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </button>
          <button
            onClick={() => onSave(true)}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            {saving
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update & Run"
                : "Save & Run"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewStep;
